import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Shield, Loader2, Eye, EyeOff, CheckCircle, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";

const schema = z.object({
  password: z
    .string()
    .min(8, "Password must be at least 8 characters")
    .regex(/[A-Z]/, "Must contain at least one uppercase letter")
    .regex(/[0-9]/, "Must contain at least one number"),
  confirmPassword: z.string(),
}).refine(d => d.password === d.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"],
});
type FormData = z.infer<typeof schema>;

export default function ResetPasswordPage() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [token, setToken] = useState("");

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const t = p.get("token");
    if (!t) {
      toast({ title: "Invalid link", description: "This reset link is invalid or has expired.", variant: "destructive" });
      setLocation("/login");
      return;
    }
    setToken(t);
  }, []);

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { password: "", confirmPassword: "" },
  });

  async function onSubmit(data: FormData) {
    setIsLoading(true);
    try {
      const res = await fetch("/api/auth/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password: data.password }),
      });
      const json = await res.json();
      if (!res.ok) {
        toast({ title: "Reset failed", description: json.error?.message || json.message || "Something went wrong. Please try again.", variant: "destructive" });
        return;
      }
      setDone(true);
    } catch {
      toast({ title: "Reset failed", description: "Something went wrong. Please try again.", variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: "var(--restore-dark-bg)" }}>
      <div className="w-full max-w-md">
        <div className="flex items-center space-x-3 mb-10 justify-center">
          <div className="rounded-xl p-2" style={{ backgroundColor: "var(--restore-accent)" }}>
            <Shield className="w-6 h-6 text-gray-900" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">RestoreAI</h1>
            <p className="text-sm" style={{ color: "var(--restore-text-muted)" }}>AI-Powered Restoration Intelligence</p>
          </div>
        </div>

        <div className="rounded-2xl border border-gray-700/50 bg-gray-900/80 p-8">
          {done ? (
            <div className="text-center">
              <div className="w-14 h-14 bg-green-500/20 rounded-full flex items-center justify-center border border-green-500/30 mx-auto mb-6">
                <CheckCircle className="w-7 h-7 text-green-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-3">Password updated</h2>
              <p className="text-gray-400 text-sm mb-8">Your password has been reset successfully. You can now sign in with your new password.</p>
              <Link href="/login">
                <Button className="text-gray-900 font-semibold" style={{ backgroundColor: "var(--restore-accent)" }}>
                  Sign in
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <h2 className="text-2xl font-bold text-white mb-2">Set new password</h2>
              <p className="text-gray-400 text-sm mb-8">Choose a strong password for your RestoreAI account.</p>

              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-gray-300">New password</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Input
                              {...field}
                              type={showPassword ? "text" : "password"}
                              placeholder="Min. 8 characters"
                              autoComplete="new-password"
                              className="bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-green-400 h-11 pr-10"
                            />
                            <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200">
                              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="confirmPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-gray-300">Confirm new password</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Input
                              {...field}
                              type={showConfirm ? "text" : "password"}
                              placeholder="Re-enter your password"
                              autoComplete="new-password"
                              className="bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-green-400 h-11 pr-10"
                            />
                            <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200">
                              {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="w-full text-gray-900 font-semibold h-11 text-base"
                    style={{ backgroundColor: "var(--restore-accent)" }}
                  >
                    {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Update password"}
                  </Button>
                </form>
              </Form>

              <div className="mt-6 text-center">
                <Link href="/login" className="text-sm text-gray-400 hover:text-gray-200 flex items-center justify-center gap-1">
                  <ArrowLeft className="w-3 h-3" />
                  Back to sign in
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
